import styled, { css } from 'styled-components'

import {
  Tooltip,
  TypeStyle,
  TypeStyleBodyMicro,
  fromMd,
  untilMd,
} from '@syconium/little-miss-figgy'

import type { EmbroideryColorFeedback } from '../../useEmbroideryColorFeedback'

export const FormBody = styled.form`
  ${untilMd} {
    padding: ${o => o.theme.spacing(2.5)} ${o => o.theme.spacing(4)} ${o => o.theme.spacing(5)};
  }

  ${fromMd} {
    border-top: 1px solid ${o => o.theme.color.stroke.neutral.border.decoration.on.background};
  }
`

export const Label = styled.h3<{
  inset?: boolean
  disabled?: boolean
}>`
  font-size: 12px;
  font-weight: bold;
  letter-spacing: 1.8px;
  margin-bottom: ${o => o.theme.spacing(2)};
  text-transform: uppercase;
  color: ${o => (o.disabled ? o.theme.color.stroke.disabled.text.on.background : 'inherit')};
`

export const PreferenceLabel = styled(Label)`
  font-size: 10px;
  margin-bottom: 0px;
  margin-left: ${o => o.theme.spacing(1)};
  vertical-align: center;
`

export const PreferenceToggleContainer = styled.div`
  display: flex;
  align-items: center;
  gap: ${o => o.theme.spacing(2)};
`
export const PreferenceTooltipButtonWrapper = styled.button`
  display: inline-flex;
  align-items: center;
`

export const SelectedOptionText = styled.span<{ disabled?: boolean }>`
  ${TypeStyle.css.headlineMicro}
  color: ${o => o.theme.color.text.secondary.on.background};
  text-transform: none;
  font-weight: ${o => o.theme.typography.font.weight[400]};
  ${o => (o.disabled ? 'visibility: hidden;' : null)}
`

export const EmbroideryOptionWrapper = styled.div`
  margin-top: ${o => o.theme.spacing(6)};
`

export const IconPlacementWrapper = styled(EmbroideryOptionWrapper)`
  margin-top: ${o => o.theme.spacing(2)};
`

export const EmbroideryInputs = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${o => o.theme.spacing(2.5)};
`

export const SelectionButton = styled.button<{
  fontFamily?: string
  isSelected: boolean
}>`
  background-color: ${o =>
    o.isSelected ? o.theme.color.fill.strong : o.theme.color.fill.background};
  border-color: ${o =>
    o.isSelected ? 'black' : o.theme.color.stroke.neutral.border.decoration.on.strong};
  border-style: solid;
  border-width: 1px;
  border-radius: ${o => o.theme.spacing(1)};
  color: ${o => (o.isSelected ? 'white' : o.theme.color.text.primary.on.background)};
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: ${o => o.fontFamily ?? 'inherit'};
  font-size: 11px;
  font-weight: bold;
  height: 40px;
  letter-spacing: 0.5px;
  outline: none;
  width: ${o => o.theme.spacing(30)};

  &:focus {
    outline: 2px solid ${props => props.theme.color.effect.focus.outline.on.background};
    outline-offset: 2px;
  }

  &:not([aria-disabled]) {
    &:hover {
      background-color: ${o =>
        o.isSelected ? o.theme.color.fill.strong : o.theme.color.fill.subtle};
    }
  }
  &[aria-disabled='true'] {
    cursor: not-allowed;
  }
`

export const ErrorMessage = styled.span<{
  inset?: boolean
}>`
  color: ${o => o.theme.color.text.critical.on.background};
  display: block;
  font-size: 11px;
  font-weight: bold;
  letter-spacing: 1px;
  margin-top: 10px;
`

export const WarningMessage = styled(TypeStyleBodyMicro)<{
  $tone: EmbroideryColorFeedback['tone']
}>`
  display: flex;
  color: ${o =>
    o.$tone === 'positive'
      ? o.theme.color.stroke.info.text.on.background
      : o.theme.color.stroke.caution.text.on.background};
  gap: ${o => o.theme.spacing(1)};
  align-items: center;
`

export const SelectionGroup = styled.div<{ $disabled?: boolean }>`
  display: flex;
  flex-wrap: wrap;
  opacity: ${o => (o.$disabled ? 0.5 : 1)};
  gap: ${o => o.theme.spacing(1)};
`

export const ThreadEmojiWrapper = styled.span`
  font-size: 18px;
  margin-top: 4px;
`

export const StyledTooltip = styled(Tooltip)`
  ${TypeStyle.css.bodySmall}
  font-weight: ${o => o.theme.typography.font.weight[400]};
  text-transform: none;
`

export const PriceLabel = styled(TypeStyle.HeadlineMicro)`
  font-weight: ${o => o.theme.typography.font.weight[700]};
  line-height: 20px;
  font-size: 13px;
`

/**
 * Stretches a visually hidden input over its positioned ancestor, so a screen reader's cursor
 * boxes the whole control instead of a 1px input away from the drawn one.
 */
export const inputFillsAncestor = css`
  && input {
    clip: auto;
    height: 100%;
    inset: 0;
    margin: 0;
    opacity: 0;
    width: 100%;
  }
`
